import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import InViewReveal from "./InViewReveal";
import SectionWatermark from "./SectionWatermark";
import CatalogueCTA from "./CatalogueCTA";

/**
 * Divisions - laid out as an editorial spread of "exhibits."
 * Each division is a tall card: large natural product photo, a folio
 * letter (A/B/C/D), title in display serif, italic strapline, mono
 * caption row at the bottom.
 *
 * Hover reveals a thin vermilion rule under the title.
 */

const DIVISIONS = [
  {
    id: "rice",
    letter: "A",
    title: "Rice & Grain",
    italic: "the staple of every shipment",
    body: "Long-grain basmati, sona masuri, idli rice, sortex-cleaned and bagged to buyer spec with lot-level traceability.",
    photo: "/hero-rice.jpg",
    note: "Long-grain · Sona Masuri · Idli",
    lines: ["1121 basmati", "1509 basmati", "Sona Masuri", "IR64", "Idli rice"],
    details: [
      ["Origin desk", "Gujarat · Andhra Pradesh · Punjab"],
      ["Grade options", "Raw, steam, parboiled, sortex, 5-25% broken"],
      ["Packing", "5 / 10 / 25 / 50 kg PP, BOPP, jute, private label"],
      ["Papers", "FSSAI, APEDA, COO, phytosanitary, lab report"],
    ],
  },
  {
    id: "spice",
    letter: "B",
    title: "Spice & Masala",
    italic: "ground at origin, sealed for sea",
    body: "Single-origin whole and ground spices, cleaned, graded, and packed with aroma retention for long-haul shipment.",
    photo: "/hero-spices.jpg",
    note: "Whole · Ground · Bespoke blends",
    lines: ["Chilli", "Turmeric", "Cumin", "Coriander", "Garam masala"],
    details: [
      ["Origin desk", "Gujarat · Rajasthan · Andhra Pradesh"],
      ["Grade options", "Whole, powder, steam-sterilized, oil-content tested"],
      ["Packing", "100 g retail to 25 kg bulk; PET, pouch, carton, sacks"],
      ["Papers", "FSSAI, Spices Board, COA, pesticide residue report"],
    ],
  },
  {
    id: "dry-fruit",
    letter: "C",
    title: "Dry Fruit & Pulse",
    italic: "weighed by the kilo, judged by the eye",
    body: "Graded nuts, raisins, figs, walnuts, and pulses selected for clean sizing, moisture control, and retail readiness.",
    photo: "/products/dry-fruits-premium.jpg",
    photoAlt: "Premium mixed dry fruits and nuts selected for export",
    note: "W180+ Cashew · Pulses · Premium nuts",
    lines: ["Cashew W180-W400", "Almond", "Raisin", "Fig", "Toor / Chana / Moong"],
    details: [
      ["Origin desk", "Gujarat · Maharashtra · Karnataka · Kashmir"],
      ["Grade options", "Whole, split, machine-cleaned, calibrated counts"],
      ["Packing", "Vacuum, nitrogen-flushed, tins, cartons, 10-25 kg bulk"],
      ["Papers", "FSSAI, aflatoxin report, COO, third-party inspection"],
    ],
  },
  {
    id: "produce",
    letter: "D",
    title: "Fresh Produce",
    italic: "cold-chain, never compromised",
    body: "Seasonal fruit and vegetables moved through pre-cooling, grading, and reefer planning before cargo reaches port.",
    photo: "/products/fresh-mangoes.jpg",
    photoAlt: "Premium mangoes selected for fresh produce export",
    photoLoop: [
      {
        src: "/products/fresh-mangoes.jpg",
        alt: "Premium mangoes selected for fresh produce export",
      },
      {
        src: "/products/fresh-onions.jpg",
        alt: "Large fresh red onion selected for export packing",
        objectPosition: "50% 62%",
      },
    ],
    note: "Reefer · CA · Seasonal",
    lines: ["Kesar mango", "Alphonso", "Pomegranate", "Garlic", "Onion"],
    details: [
      ["Origin desk", "Gujarat · Maharashtra · Karnataka · Nashik"],
      ["Grade options", "A/B sizing, residue-tested, export carton ready"],
      ["Packing", "Corrugated cartons, mesh bags, reefer pallets, CA-ready"],
      ["Papers", "APEDA, phytosanitary, farm traceability, reefer log"],
    ],
  },
];

export default function ProductShowcase() {
  return (
    <section id="products" className="relative paper" style={{ overflow: "hidden" }}>
      <SectionWatermark variant="monogram" position="top-right" size={420} opacity={0.18} />
      <div className="relative px-6 sm:px-10 py-14 md:py-20" style={{ zIndex: 1 }}>

        <InViewReveal className="flex items-baseline gap-4 mb-8 md:mb-12">
          <span className="folio">№ 02</span>
          <span className="rule rule-grow" style={{ flex: 1, height: 1 }} />
          <span className="folio" style={{ color: "var(--ink-mute)" }}>FOUR DIVISIONS</span>
        </InViewReveal>

        {/* Section title block */}
        <div className="grid md:grid-cols-12 gap-10 md:gap-12 mb-10 md:mb-14">
          <InViewReveal delay={2} className="md:col-span-7 lg:col-span-7">
            <h2 className="display" style={{ fontSize: "clamp(40px, 5vw, 72px)", lineHeight: 1, letterSpacing: "-0.02em" }}>
              Four houses,
              <br/>
              <em className="display-italic" style={{ color: "var(--vermilion)" }}>one ledger.</em>
            </h2>
          </InViewReveal>
          <InViewReveal delay={3} className="md:col-span-5 lg:col-span-4 lg:col-start-9">
            <p style={{ fontSize: "clamp(17px, 1.25vw, 20px)", lineHeight: 1.65, color: "var(--ink-soft)" }}>
              Each desk is run by people who know the buyers by name - and who pick up
              the phone before the cargo leaves origin.
            </p>
            <CatalogueCTA className="mt-6" code="LINESHEET" compact />
          </InViewReveal>
        </div>

        {/* Exhibit grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {DIVISIONS.map((d, i) => (
            <Exhibit key={d.id} d={d} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Exhibit({ d, idx }) {
  const [hovered, setHovered] = useState(false);
  return (
    <InViewReveal delay={Math.min(idx + 1, 4)} as="article"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="group relative transition-transform duration-500"
      style={{
        background: "var(--paper-1)",
        border: "1px solid var(--rule)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered
          ? "4px 4px 0 0 var(--brass), 0 12px 28px -16px rgba(26,31,54,0.25)"
          : "0 0 0 0 transparent",
      }}
    >
      {/* Tiny corner registration marks */}
      <ExhibitCorner pos="tl" />
      <ExhibitCorner pos="tr" />
      <ExhibitCorner pos="bl" />
      <ExhibitCorner pos="br" />

      {/* Photo */}
      <div className="duotone duotone--natural relative" style={{ aspectRatio: "4 / 5", background: "var(--paper-2)" }}>
        {d.photoLoop?.length ? (
          <div className="division-photo-loop" aria-label={`${d.title} product image loop`}>
            {d.photoLoop.map((photo, photoIndex) => (
              <img
                key={photo.src}
                src={photo.src}
                alt={photo.alt}
                loading={photoIndex === 0 ? "eager" : "lazy"}
                className="division-photo-loop-img"
                style={{
                  animationDelay: `${photoIndex * 3.6}s`,
                  animationDuration: `${d.photoLoop.length * 3.6}s`,
                  objectPosition: photo.objectPosition || "50% 50%",
                }}
              />
            ))}
          </div>
        ) : (
          <img src={d.photo} alt={d.photoAlt || d.title} loading="lazy" />
        )}

        {/* Plate caption - top-left */}
        <span
          className="absolute top-3 left-3 z-10 folio-chip"
          style={{ background: "var(--bone)" }}
        >
          PLATE {String.fromCharCode(73 + idx /* I, II, III, IV */)}
        </span>

        {/* Hover-only stamp - vermilion "in season / on file" mark */}
        <span
          className="absolute top-3 right-3 z-10 ink-stamp transition-all duration-500"
          style={{
            background: "rgba(244,239,227,0.92)",
            fontSize: 11,
            padding: "4px 10px",
            opacity: hovered ? 1 : 0,
            transform: hovered ? "rotate(-6deg) scale(1)" : "rotate(-12deg) scale(0.8)",
          }}
        >
          ON FILE
        </span>

        {/* Big italic letter - bottom-right */}
        <span
          className="display-italic absolute bottom-3 right-4 z-10"
          style={{
            color: "var(--bone)",
            fontSize: 72,
            lineHeight: 0.85,
            opacity: 0.92,
            letterSpacing: "-0.04em",
            textShadow: "0 1px 0 rgba(0,0,0,0.25)",
          }}
        >
          {d.letter}
        </span>

        {/* Bottom-left mono code */}
        <span
          className="absolute bottom-3 left-3 z-10 mono"
          style={{
            fontSize: 11,
            letterSpacing: "0.12em",
            color: "var(--bone)",
            opacity: 0.75,
          }}
        >
          {String(idx + 1).padStart(3, "0")} / 004
        </span>
      </div>

      {/* Caption block */}
      <div className="p-6 relative">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="display" style={{ fontSize: 24, lineHeight: 1.1, letterSpacing: "-0.01em" }}>
            {d.title}
          </h3>
          <span className="status-dot status-dot--brass" title="Stocked"/>
        </div>
        <div
          className="display-italic mt-1"
          style={{ fontSize: 16, color: "var(--ink-mute)" }}
        >
          {d.italic}
        </div>
        <div
          className="rule mt-4 mb-4"
          style={{
            background: hovered ? "var(--vermilion)" : "var(--rule)",
            transition: "background 350ms ease, transform 360ms cubic-bezier(0.2,0.8,0.2,1)",
            transformOrigin: "left",
            transform: hovered ? "scaleX(1)" : "scaleX(0.32)",
            height: hovered ? 1.5 : 1,
          }}
        />
        <p style={{ fontSize: 15.5, lineHeight: 1.6, color: "var(--ink-soft)" }}>
          {d.body}
        </p>
        <div className="division-lines mt-4" aria-label={`${d.title} product lines`}>
          {d.lines.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </div>
        <div className="division-specs mt-5">
          {d.details.map(([label, value]) => (
            <div className="division-spec-row" key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
        <div
          className="division-card-footer flex items-center justify-between mt-5 pt-3 border-t"
          style={{ borderColor: "var(--rule-soft)" }}
        >
          <div className="mono" style={{ fontSize: 11.5, letterSpacing: "0.12em", color: "var(--ink-mute)" }}>
            {d.note}
          </div>
          <Link
            to="/catalogue"
            className="division-download"
            aria-label={`View catalogue for ${d.title}`}
            onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "auto" })}
          >
            <BookOpen size={14} strokeWidth={1.5} />
            <span>View Catalogue</span>
          </Link>
        </div>
      </div>
    </InViewReveal>
  );
}

/** Tiny brass corner registration mark on each exhibit card. */
function ExhibitCorner({ pos }) {
  const map = {
    tl: { top: 6,    left: 6,   transform: "rotate(0deg)" },
    tr: { top: 6,    right: 6,  transform: "rotate(90deg)" },
    bl: { bottom: 6, left: 6,   transform: "rotate(-90deg)" },
    br: { bottom: 6, right: 6,  transform: "rotate(180deg)" },
  };
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" className="absolute z-20 pointer-events-none" style={map[pos]}>
      <path d="M0 0 L0 6 M0 0 L6 0" stroke="var(--brass)" strokeWidth="0.7" fill="none"/>
    </svg>
  );
}
